import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TenantToggleButton } from './TenantToggleButton';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  SearchIcon,
  BuildingIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  RefreshCwIcon,
  XCircleIcon,
  DownloadIcon,
  UsersIcon,
  CreditCardIcon,
  TrendingUpIcon,
  CalendarIcon,
  FilterIcon,
  MailIcon,
  XIcon,
  SendIcon,
  EyeIcon,
  EyeOffIcon,
  CopyIcon,
  CheckIcon,
  MessageCircleIcon,
  LayoutGrid,
  List,
  InfoIcon,
  CrownIcon,
  PowerIcon,
  UserXIcon,
  AlertTriangleIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useUltraAdminContext } from '../UltraAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UltraTenantManagementNew = () => {
  const [tenants, setTenants] = useState([]);
  const [deletedTenants, setDeletedTenants] = useState([]);
  const [suspendedTenants, setSuspendedTenants] = useState([]);
  const [invitedTenants, setInvitedTenants] = useState([]);
  const [inviteCount, setInviteCount] = useState(0);
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [sortBy, setSortBy] = useState('users_desc'); // users_desc, users_asc, name_asc, date_desc
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [itemsPerPage, setItemsPerPage] = useState(25); // 25, 50, or 100
  const [confirmModal, setConfirmModal] = useState({ show: false, tenantId: null, action: null, tenantName: '' });
  const [detailModal, setDetailModal] = useState({ show: false, tenant: null, loading: false });
  const [usersModal, setUsersModal] = useState({ show: false, users: [], companyName: '', loading: false, tenantId: null });
  const [userActionModal, setUserActionModal] = useState({
    show: false,
    user: null,
    action: null, // 'deactivate' | 'activate' | 'delete'
    check: null,  // { can_delete, transaction_count, breakdown, reason }
    loading: false,
  });
  const [inviteModal, setInviteModal] = useState({ 
    show: false, 
    step: 'form', // form, preview, success
    data: {
      email: '',
      maxUsers: 5,
      trialDays: 14,
      companyName: '',
      message: ''
    },
    result: null,
    loading: false,
    emailError: '' // Add email validation error
  });
  
  // Yeni Kiracı Oluşturma Modal State
  const [createTenantModal, setCreateTenantModal] = useState({
    show: false,
    loading: false,
    step: 'company', // company, admin, success
    data: {
      // Şirket Bilgileri
      companyName: '',
      taxNumber: '',
      taxOffice: '',
      address: '',
      phone: '',
      email: '',
      // Admin Kullanıcı Bilgileri
      adminFullName: '',
      adminEmail: '',
      adminPassword: '',
      adminPasswordConfirm: '',
      // Plan Bilgileri
      maxUsers: 10,
      trialDays: 14,
      plan: 'trial'
    },
    result: null,
    errors: {},
    showPassword: false, // Şifre görünürlüğü
    showPasswordConfirm: false // Şifre tekrar görünürlüğü
  });
  
  const context = useUltraAdminContext();

  // Initial load: fetch all counts
  useEffect(() => {
    fetchTenants();
    fetchDeletedTenants();
    fetchSuspendedTenants();
    fetchInvitedTenants();
  }, []);

  // Refetch when tab changes
  useEffect(() => {
    if (activeTab === 'active') {
      fetchTenants();
    } else if (activeTab === 'deleted') {
      fetchDeletedTenants();
    } else if (activeTab === 'invited') {
      fetchInvitedTenants();
    }
  }, [activeTab]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const tenantsResponse = await axios.get(`${BACKEND_URL}/api/ultra-admin/tenants`, config);
      setTenants(tenantsResponse.data.tenants);
    } catch (error) {
      toast.error('Kiracılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/tenants/deleted`, config);
      setDeletedTenants(response.data.tenants);
    } catch (error) {
      console.error('Error fetching deleted tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuspendedTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/tenants/suspended`, config);
      setSuspendedTenants(response.data.tenants);
    } catch (error) {
      console.error('Error fetching suspended tenants:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchInvitedTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      console.log('🔍 Fetching invited tenants...');
      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/invites`, config);
      console.log('📋 Invites response:', response.data);
      
      const invites = response.data.invites || [];
      const total = response.data.total || invites.length;
      console.log(`📊 Found ${invites.length} invites, total: ${total}`);
      
      setInvitedTenants(invites);
      setInviteCount(total);
    } catch (error) {
      console.error('❌ Invite fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const showConfirmModal = (tenantId, action, tenantName) => {
    setConfirmModal({ show: true, tenantId, action, tenantName });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ show: false, tenantId: null, action: null, tenantName: '' });
  };

  const handleTenantAction = async (tenantId, action) => {
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.post(`${BACKEND_URL}/api/ultra-admin/tenants/${tenantId}/actions`, { action }, config);
      
      const successMessages = {
        'delete': 'Kiracı başarıyla silindi',
        'suspend': 'Kiracı başarıyla askıya alındı',
        'activate': 'Kiracı başarıyla aktifleştirildi'
      };
      
      // Immediately refresh lists (optimistic update)
      if (action === 'suspend') {
        fetchTenants();
        fetchSuspendedTenants();
      } else if (action === 'activate') {
        fetchTenants();
        fetchSuspendedTenants();
      } else if (action === 'delete') {
        fetchDeletedTenants();
      }
      
      // Show success message in modal
      setConfirmModal(prev => ({
        ...prev,
        status: 'success',
        message: successMessages[action] || 'İşlem başarılı'
      }));
      
      // Close modal after 2 seconds and refresh again
      setTimeout(() => {
        closeConfirmModal();
        
        // Refresh lists again to ensure consistency
        if (action === 'delete') {
          fetchTenants();
          fetchDeletedTenants();
        } else if (action === 'suspend') {
          fetchTenants();
          fetchSuspendedTenants();
        } else if (action === 'activate') {
          fetchTenants();
          fetchSuspendedTenants();
        }
        
        context?.triggerRefresh();
      }, 2000);
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message;
      
      // Show error message in modal with details
      setConfirmModal(prev => ({
        ...prev,
        status: 'error',
        message: errorDetail,
        errorReason: error.response?.data?.reason || null
      }));
    }
  };

  const handleRestoreTenant = async (tenantId, tenantName) => {
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${BACKEND_URL}/api/ultra-admin/tenants/${tenantId}/restore`, {}, config);
      
      toast.success(`${tenantName} kiracısı geri yüklendi`);
      fetchDeletedTenants();
      context?.triggerRefresh();
    } catch (error) {
      toast.error(`Geri yükleme başarısız: ${error.response?.data?.detail || error.message}`);
    }
  };
  const handleInviteSubmit = async () => {
    const { email, maxUsers, trialDays, companyName, message } = inviteModal.data;
    
    // Validation
    if (!email.trim()) {
      toast.error('E-posta adresi gerekli');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Geçerli bir e-posta adresi girin');
      return;
    }
    
    if (maxUsers < 1 || maxUsers > 100) {
      toast.error('Kullanıcı sayısı 1-100 arasında olmalı');
      return;
    }

    setInviteModal(prev => ({ ...prev, loading: true }));

    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/invites/send`,
        {
          email: email.trim(),
          max_users: parseInt(maxUsers),
          trial_days: parseInt(trialDays),
          company_name: companyName.trim() || null,
          message: message.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setInviteModal(prev => ({
        ...prev,
        step: 'success',
        result: response.data,
        loading: false
      }));

      toast.success('Davet başarıyla gönderildi!');
      
    } catch (error) {
      console.error('Invite send error:', error);
      toast.error(error.response?.data?.detail || 'Davet gönderilirken hata oluştu');
      setInviteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const copyInviteLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Davet linki kopyalandı!');
  };

  // Fetch tenant details
  const fetchTenantDetails = async (tenantId) => {
    setDetailModal({ show: true, tenant: null, loading: true });
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/tenants/${tenantId}/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetailModal({ show: true, tenant: response.data, loading: false });
    } catch (error) {
      console.error('Fetch tenant details error:', error);
      toast.error('Kiracı detayları yüklenemedi');
      setDetailModal({ show: false, tenant: null, loading: false });
    }
  };

  // Fetch tenant users
  const fetchTenantUsers = async (tenantId, companyName) => {
    setUsersModal({ show: true, users: [], companyName, loading: true, tenantId });
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/tenants/${tenantId}/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsersModal({ show: true, users: response.data.users, companyName, loading: false, tenantId });
    } catch (error) {
      console.error('Fetch tenant users error:', error);
      toast.error('Kullanıcılar yüklenemedi');
      setUsersModal({ show: false, users: [], companyName: '', loading: false, tenantId: null });
    }
  };

  // Reload users in the currently open modal
  const reloadUsersModal = async () => {
    if (!usersModal.tenantId) return;
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/tenants/${usersModal.tenantId}/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsersModal((prev) => ({ ...prev, users: response.data.users }));
    } catch (error) {
      console.error('Reload users error:', error);
    }
  };

  // Open user action modal with fresh transaction check
  const openUserAction = async (user, action) => {
    setUserActionModal({ show: true, user, action, check: null, loading: true });
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/users/${user.id}/deletion-check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserActionModal({ show: true, user, action, check: response.data, loading: false });
    } catch (error) {
      console.error('Deletion check error:', error);
      setUserActionModal({ show: true, user, action, check: null, loading: false });
    }
  };

  // Execute activate / deactivate / delete
  const executeUserAction = async () => {
    const { user, action } = userActionModal;
    if (!user || !action) return;
    try {
      setUserActionModal((prev) => ({ ...prev, loading: true }));
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/users/${user.id}/actions`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data?.message || 'İşlem başarılı');
      setUserActionModal({ show: false, user: null, action: null, check: null, loading: false });
      await reloadUsersModal();
    } catch (error) {
      const detail = error.response?.data?.detail || 'İşlem başarısız';
      const canDeactivate = error.response?.headers?.['x-can-deactivate'] === 'true';
      toast.error(detail);
      if (canDeactivate) {
        // Kullanıcının işlem geçmişi varsa → doğrudan pasife alma seçeneğine geç
        try {
          const token = localStorage.getItem('ultra_admin_token');
          const res = await axios.get(
            `${BACKEND_URL}/api/ultra-admin/users/${user.id}/deletion-check`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setUserActionModal({ show: true, user, action: 'deactivate', check: res.data, loading: false });
        } catch {
          setUserActionModal((prev) => ({ ...prev, loading: false }));
        }
      } else {
        setUserActionModal((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  // Login as user (passwordless impersonation)
  const handleLoginAsUser = async (user) => {
    try {
      const token = localStorage.getItem('ultra_admin_token');
      
      // Show loading toast
      const loadingToast = toast.loading(`${user.full_name} olarak giriş yapılıyor...`);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/impersonate-passwordless/${user.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Save impersonation data
        const impersonationData = {
          isImpersonated: true,
          adminToken: token, // Save Ultra Admin token to return later
          adminEmail: response.data.impersonation.admin_email,
          targetUser: {
            id: user.id,
            email: user.email,
            full_name: user.full_name,
            company_name: response.data.user.company_name
          }
        };
        
        localStorage.setItem('impersonation', JSON.stringify(impersonationData));
        localStorage.setItem('token', response.data.access_token);
        
        toast.dismiss(loadingToast);
        toast.success(`${user.full_name} olarak giriş yapıldı`, {
          description: 'Kullanıcı paneline yönlendiriliyorsunuz...'
        });
        
        // Redirect to main dashboard
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);
      }
    } catch (error) {
      console.error('Login as user error:', error);
      toast.error('Giriş yapılamadı', {
        description: error.response?.data?.detail || 'Bir hata oluştu'
      });
    }
  };

  // Export tenants to CSV
  const exportToCSV = () => {
    try {
      // Prepare data based on active tab
      let dataToExport = [];
      let filename = '';
      
      if (activeTab === 'active') {
        dataToExport = filteredTenants;
        filename = 'aktif_kiracilar';
      } else if (activeTab === 'deleted') {
        dataToExport = deletedTenants;
        filename = 'silinen_kiracilar';
      } else if (activeTab === 'invited') {
        dataToExport = invitedTenants;
        filename = 'davet_edilenler';
      }

      if (dataToExport.length === 0) {
        toast.error('Dışa aktarılacak veri bulunamadı');
        return;
      }

      // Create CSV content
      let csvContent = '';
      
      if (activeTab === 'active' || activeTab === 'deleted') {
        // Headers for tenants
        csvContent = 'Şirket Adı,Email,Plan,Durum,Kullanıcı,Kullanıcı Limiti,Aylık Ücret,Kayıt Tarihi\n';
        
        // Data rows
        dataToExport.forEach(tenant => {
          const row = [
            tenant.company_name || '',
            tenant.owner_email || '',
            tenant.subscription_plan || '',
            tenant.status || '',
            tenant.user_count || 0,
            tenant.user_limit || 0,
            tenant.monthly_fee || 0,
            tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('tr-TR') : ''
          ].map(field => `"${field}"`).join(',');
          
          csvContent += row + '\n';
        });
      } else if (activeTab === 'invited') {
        // Headers for invites
        csvContent = 'Email,Şirket,Max Kullanıcı,Deneme Süresi,Durum,Gönderim Tarihi,Son Tarih\n';
        
        // Data rows
        dataToExport.forEach(invite => {
          const row = [
            invite.email || '',
            invite.company_name || '',
            invite.max_users || 0,
            invite.trial_days || 0,
            invite.status || '',
            invite.created_at ? new Date(invite.created_at).toLocaleDateString('tr-TR') : '',
            invite.expires_at ? new Date(invite.expires_at).toLocaleDateString('tr-TR') : ''
          ].map(field => `"${field}"`).join(',');
          
          csvContent += row + '\n';
        });
      }

      // Create blob and download
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`${dataToExport.length} kayıt CSV olarak dışa aktarıldı`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Dışa aktarma sırasında hata oluştu');
    }
  };

  const resetInviteModal = () => {
    setInviteModal({
      show: false,
      step: 'form',
      data: {
        email: '',
        maxUsers: 5,
        trialDays: 14,
        companyName: '',
        message: ''
      },
      result: null,
      loading: false,
      emailError: ''
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Yeni Kiracı Oluşturma
  const handleCreateTenant = async () => {
    const { data, errors } = createTenantModal;
    
    // Validation
    const newErrors = {};
    
    if (createTenantModal.step === 'company') {
      if (!data.companyName) newErrors.companyName = 'Şirket adı gerekli';
      if (!data.email) newErrors.email = 'Şirket email gerekli';
      else if (!validateEmail(data.email)) newErrors.email = 'Geçerli bir email girin';
      if (!data.phone) newErrors.phone = 'Telefon gerekli';
    } else if (createTenantModal.step === 'admin') {
      if (!data.adminFullName) newErrors.adminFullName = 'Ad Soyad gerekli';
      if (!data.adminEmail) newErrors.adminEmail = 'Email gerekli';
      else if (!validateEmail(data.adminEmail)) newErrors.adminEmail = 'Geçerli bir email girin';
      if (!data.adminPassword) newErrors.adminPassword = 'Şifre gerekli';
      else if (data.adminPassword.length < 6) newErrors.adminPassword = 'Şifre en az 6 karakter olmalı';
      if (data.adminPassword !== data.adminPasswordConfirm) newErrors.adminPasswordConfirm = 'Şifreler eşleşmiyor';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setCreateTenantModal(prev => ({ ...prev, errors: newErrors }));
      return;
    }
    
    // İlk step'te: bir sonraki adıma geç
    if (createTenantModal.step === 'company') {
      setCreateTenantModal(prev => ({ ...prev, step: 'admin', errors: {} }));
      return;
    }
    
    // Son step'te: Backend'e gönder
    setCreateTenantModal(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/tenants/create`,
        {
          company_name: data.companyName,
          tax_number: data.taxNumber,
          tax_office: data.taxOffice,
          address: data.address,
          phone: data.phone,
          email: data.email,
          admin_full_name: data.adminFullName,
          admin_email: data.adminEmail,
          admin_password: data.adminPassword,
          max_users: data.maxUsers,
          trial_days: data.trialDays,
          plan: data.plan
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setCreateTenantModal(prev => ({
          ...prev,
          step: 'success',
          result: response.data,
          loading: false
        }));
        
        // Refresh tenant list
        fetchTenants();
        toast.success('Kiracı başarıyla oluşturuldu!');
      }
    } catch (error) {
      console.error('Create tenant error:', error);
      // Handle error detail - might be object or string
      let errorMessage = 'Kiracı oluşturma hatası';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      }
      toast.error(errorMessage);
      setCreateTenantModal(prev => ({ ...prev, loading: false }));
    }
  };
  
  const resetCreateTenantModal = () => {
    setCreateTenantModal({
      show: false,
      loading: false,
      step: 'company',
      data: {
        companyName: '',
        taxNumber: '',
        taxOffice: '',
        address: '',
        phone: '',
        email: '',
        adminFullName: '',
        adminEmail: '',
        adminPassword: '',
        adminPasswordConfirm: '',
        maxUsers: 10,
        trialDays: 14,
        plan: 'trial'
      },
      result: null,
      errors: {}
    });
  };


  const handleEmailBlur = () => {
    if (!inviteModal.data) return;
    
    const email = inviteModal.data.email?.trim() || '';
    if (email && !validateEmail(email)) {
      setInviteModal(prev => ({
        ...prev,
        emailError: 'Geçerli bir e-posta adresi girin'
      }));
    } else {
      setInviteModal(prev => ({
        ...prev,
        emailError: ''
      }));
    }
  };

  const updateInviteData = (field, value) => {
    setInviteModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value
      },
      // Clear email error when user types
      ...(field === 'email' ? { emailError: '' } : {})
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', className: 'bg-green-100 text-green-700' },
      trial: { label: 'Deneme', className: 'bg-blue-100 text-blue-700' },
      suspended: { label: 'Askıda', className: 'bg-yellow-100 text-yellow-700' },
      expired: { label: 'Süresi Dolmuş', className: 'bg-red-100 text-red-700' }
    };
    
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getPlanBadge = (plan) => {
    const planConfig = {
      free_trial: { label: 'Free Trial', icon: '🆓' },
      starter: { label: 'Starter', icon: '🥉' },
      professional: { label: 'Professional', icon: '🥈' },
      enterprise: { label: 'Enterprise', icon: '🥇' }
    };
    
    const config = planConfig[plan] || { label: plan, icon: '📦' };
    return (
      <Badge variant="outline" className="font-medium">
        {config.icon} {config.label}
      </Badge>
    );
  };

  // Filter and sort tenants
  const getFilteredAndSortedTenants = () => {
    let filtered = activeTab === 'active' ? tenants : 
                   activeTab === 'suspended' ? suspendedTenants :
                   activeTab === 'deleted' ? deletedTenants : 
                   invitedTenants;
    
    // Apply filters
    filtered = filtered.filter(tenant => {
      const companyName = tenant.company_name || '';
      const ownerEmail = tenant.owner_email || '';
      const matchesSearch = companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           ownerEmail.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;
      const matchesPlan = planFilter === 'all' || tenant.subscription_plan === planFilter;
      
      return matchesSearch && matchesStatus && matchesPlan;
    });

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'users_desc':
          return (b.user_count || 0) - (a.user_count || 0);
        case 'users_asc':
          return (a.user_count || 0) - (b.user_count || 0);
        case 'name_asc':
          return (a.company_name || '').localeCompare(b.company_name || '');
        case 'date_desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return 0;
      }
    });

    return sorted;
  };

  const filteredTenants = getFilteredAndSortedTenants();

  // Calculate stats
  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.status === 'active').length,
    trial: tenants.filter(t => t.status === 'trial').length,
    suspended: tenants.filter(t => t.status === 'suspended').length,
    totalUsers: tenants.reduce((sum, t) => sum + t.user_count, 0),
    totalRevenue: tenants.reduce((sum, t) => sum + (t.monthly_fee || 0), 0)
  };

  // Pagination
  const indexOfLastTenant = currentPage * itemsPerPage;
  const indexOfFirstTenant = indexOfLastTenant - itemsPerPage;
  const currentTenants = filteredTenants.slice(indexOfFirstTenant, indexOfLastTenant);
  const totalPages = Math.ceil(filteredTenants.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCwIcon className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Kiracılar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Kiracı Yönetimi</h2>
            <p className="text-gray-600 mt-1">Tüm şirket hesaplarını yönetin ve izleyin</p>
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={() => setCreateTenantModal(prev => ({ ...prev, show: true }))}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <BuildingIcon className="w-4 h-4 mr-2" />
              Yeni Kiracı Oluştur
            </Button>
            <Button 
              onClick={() => setInviteModal(prev => ({ ...prev, show: true }))}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <MailIcon className="w-4 h-4 mr-2" />
              Davet Gönder
            </Button>
            <Button variant="outline" onClick={() => {
              if (activeTab === 'active') {
                fetchTenants();
              } else if (activeTab === 'deleted') {
                fetchDeletedTenants();
              } else if (activeTab === 'invited') {
                fetchInvitedTenants();
              }
            }}>
              <RefreshCwIcon className="w-4 h-4 mr-2" />
              Yenile
            </Button>
            <Button 
              variant="outline"
              onClick={exportToCSV}
              title="CSV formatında dışa aktar"
            >
              <DownloadIcon className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Toplam Kiracı</p>
                <p className="text-3xl font-bold mt-1">{stats.total}</p>
                <p className="text-blue-100 text-xs mt-1">{stats.active} aktif</p>
              </div>
              <BuildingIcon className="w-12 h-12 text-blue-300 opacity-80" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Toplam Kullanıcı</p>
                <p className="text-3xl font-bold mt-1">{stats.totalUsers}</p>
                <p className="text-green-100 text-xs mt-1">Tüm kiracılar</p>
              </div>
              <UsersIcon className="w-12 h-12 text-green-300 opacity-80" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Aylık Gelir</p>
                <p className="text-3xl font-bold mt-1">₺{stats.totalRevenue}</p>
                <p className="text-purple-100 text-xs mt-1">Toplam MRR</p>
              </div>
              <TrendingUpIcon className="w-12 h-12 text-purple-300 opacity-80" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Deneme Süreci</p>
                <p className="text-3xl font-bold mt-1">{stats.trial}</p>
                <p className="text-orange-100 text-xs mt-1">Aktif denemeler</p>
              </div>
              <CalendarIcon className="w-12 h-12 text-orange-300 opacity-80" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'active'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('active');
              setCurrentPage(1);
            }}
          >
            Aktif Kiracılar ({tenants.length})
          </button>
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'suspended'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('suspended');
              setCurrentPage(1);
            }}
          >
            Pasif Kiracılar ({suspendedTenants.length})
          </button>
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'deleted'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('deleted');
              setCurrentPage(1);
            }}
          >
            Silinen Kiracılar ({deletedTenants.length})
          </button>
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'invited'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('invited');
              setCurrentPage(1);
            }}
          >
            Davet Edilenler ({inviteCount})
          </button>
        </div>
      </Card>

      {/* Filters */}
      {activeTab === 'active' && (
        <Card className="p-4">
          <div className="flex flex-col lg:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Şirket adı veya email ara..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">🔵 Tüm Durumlar</option>
              <option value="active">✅ Aktif</option>
              <option value="trial">🎁 Deneme</option>
              <option value="suspended">⏸️ Askıda</option>
              <option value="expired">❌ Süresi Dolmuş</option>
            </select>

            <select
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">📦 Tüm Planlar</option>
              <option value="free_trial">🆓 Free Trial</option>
              <option value="starter">🥉 Starter</option>
              <option value="professional">🥈 Professional</option>
              <option value="enterprise">🥇 Enterprise</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="users_desc">👥 Kullanıcı (Çok→Az)</option>
              <option value="users_asc">👥 Kullanıcı (Az→Çok)</option>
              <option value="name_asc">🔤 İsim (A→Z)</option>
              <option value="date_desc">📅 Yeni Eklenme</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 flex items-center gap-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Grid Görünümü"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 flex items-center gap-2 transition-colors border-l border-gray-300 ${
                  viewMode === 'list'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Liste Görünümü"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span>{filteredTenants.length} kiracı gösteriliyor</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Sayfa başına:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <span className="text-purple-600 font-medium">
              {viewMode === 'grid' ? '📊 Grid Görünümü' : '📋 Liste Görünümü'}
            </span>
          </div>
        </Card>
      )}

      {/* Tenants Grid View - Active & Suspended */}
      {(activeTab === 'active' || activeTab === 'suspended') && viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentTenants.map(tenant => (
            <Card key={tenant.id} className="p-5 hover:shadow-lg transition-shadow min-w-[300px]">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0 overflow-hidden">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BuildingIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h3 className="font-semibold text-gray-900 truncate overflow-hidden" title={tenant.company_name}>{tenant.company_name}</h3>
                      <p className="text-sm text-gray-500 truncate overflow-hidden break-all" title={tenant.owner_email} style={{ wordBreak: 'break-word' }}>{tenant.owner_email}</p>
                      {tenant.tenant_number && (
                        <p className="text-xs text-purple-600 font-mono mt-1">Kiracı No: #{tenant.tenant_number}</p>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(tenant.status)}
                </div>

                {/* Info */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Plan:</span>
                    {getPlanBadge(tenant.subscription_plan)}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Kullanıcı:</span>
                    <span className="font-medium text-gray-900">
                      {tenant.user_count} / {tenant.user_limit}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Aylık Ücret:</span>
                    <span className="font-medium text-gray-900">₺{tenant.monthly_fee}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Kayıt:</span>
                    <span className="text-gray-500 text-xs">
                      {new Date(tenant.created_at).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2 pt-3 border-t">
                  <div className="flex space-x-2">
                    <TenantToggleButton
                      tenant={tenant}
                      onToggle={showConfirmModal}
                      size="sm"
                      showIcon={true}
                      showText={true}
                    />
                    {tenant.company_name !== "Ultra Admin" && !tenant.is_permanent && !tenant.is_master_template && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-red-600 hover:bg-red-50"
                        onClick={() => showConfirmModal(tenant.id, 'delete', tenant.company_name)}
                      >
                        <TrashIcon className="w-4 h-4 mr-1" />
                        Sil
                      </Button>
                    )}
                    {(tenant.company_name === "Ultra Admin" || tenant.is_master_template) && (
                      <div className="flex-1 text-center py-2 text-gray-400 text-xs">
                        {tenant.is_master_template ? '🔒 Master Template' : 'Sistem Kiracısı'}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => fetchTenantDetails(tenant.id)}
                    >
                      <InfoIcon className="w-4 h-4 mr-1" />
                      Detay
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => fetchTenantUsers(tenant.id, tenant.company_name)}
                    >
                      <UsersIcon className="w-4 h-4 mr-1" />
                      Kullanıcılar
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tenants List View - Active & Suspended */}
      {(activeTab === 'active' || activeTab === 'suspended') && viewMode === 'list' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Şirket</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kullanıcı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aylık Ücret</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kayıt Tarihi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentTenants.map(tenant => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BuildingIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0 overflow-hidden" style={{ maxWidth: '250px' }}>
                          <p className="font-medium text-gray-900 text-sm truncate overflow-hidden" title={tenant.company_name}>{tenant.company_name}</p>
                          <p className="text-xs text-gray-500 truncate overflow-hidden break-all" title={tenant.owner_email} style={{ wordBreak: 'break-word' }}>{tenant.owner_email}</p>
                          {tenant.tenant_number && (
                            <p className="text-xs text-purple-600 font-mono">#{tenant.tenant_number}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getPlanBadge(tenant.subscription_plan)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{tenant.user_count} / {tenant.user_limit}</span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(tenant.status)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">₺{tenant.monthly_fee}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {new Date(tenant.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => fetchTenantDetails(tenant.id)}
                          title="Detay"
                        >
                          <InfoIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => fetchTenantUsers(tenant.id, tenant.company_name)}
                          title="Kullanıcılar"
                        >
                          <UsersIcon className="w-4 h-4" />
                        </Button>
                        <TenantToggleButton
                          tenant={tenant}
                          onToggle={showConfirmModal}
                          size="sm"
                          showIcon={true}
                          showText={false}
                        />
                        {tenant.company_name !== "Ultra Admin" && !tenant.is_permanent && !tenant.is_master_template && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => showConfirmModal(tenant.id, 'delete', tenant.company_name)}
                            title="Sil"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        )}
                        {(tenant.company_name === "Ultra Admin" || tenant.is_master_template) && (
                          <div className="text-center py-1 text-gray-400 text-xs">
                            {tenant.is_master_template ? '🔒 Master' : 'Sistem'}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {currentTenants.length === 0 && (
            <div className="text-center py-12">
              <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Kiracı bulunamadı</h3>
              <p className="text-gray-500">Arama kriterlerinize uygun kiracı bulunmuyor.</p>
            </div>
          )}
        </Card>
      )}

      {/* Deleted Tenants */}
      {activeTab === 'deleted' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Şirket</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kullanıcı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Silinme Tarihi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deletedTenants.map(tenant => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <BuildingIcon className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{tenant.company_name}</p>
                          <Badge variant="outline" className="text-red-600 border-red-200 mt-1">Silinmiş</Badge>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{getPlanBadge(tenant.subscription_plan)}</td>
                    <td className="px-6 py-4 text-gray-900">{tenant.user_count} kullanıcı</td>
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {new Date(tenant.deleted_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleRestoreTenant(tenant.id, tenant.company_name)}
                      >
                        Geri Yükle
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {deletedTenants.length === 0 && (
            <div className="text-center py-12">
              <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Silinmiş kiracı yok</h3>
              <p className="text-gray-500">Henüz hiç kiracı silinmemiş.</p>
            </div>
          )}
        </Card>
      )}

      {/* Invited Tenants */}
      {activeTab === 'invited' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-posta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Şirket</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kullanıcı Limiti</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deneme Süresi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gönderim Tarihi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invitedTenants.map((invite, index) => (
                  <tr key={invite.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <MailIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{invite.email}</p>
                          <p className="text-sm text-gray-500">ID: {invite.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{invite.company_name || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{invite.max_users} kullanıcı</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{invite.trial_days} gün</span>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={
                        (invite.status === 'pending' || invite.status === 'sent') ? 'bg-yellow-100 text-yellow-700' :
                        invite.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        invite.status === 'expired' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {(invite.status === 'pending' || invite.status === 'sent') ? 'Bekliyor' :
                         invite.status === 'accepted' ? 'Kabul Edildi' :
                         invite.status === 'expired' ? 'Süresi Doldu' :
                         invite.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {new Date(invite.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyInviteLink(invite.invite_link)}
                          className="text-blue-600 hover:bg-blue-50"
                          title="Davet linkini kopyala"
                        >
                          <CopyIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            window.open(`mailto:${invite.email}?subject=AdvanceCash.io Davet Hatırlatması&body=Merhaba,%0A%0AAdvanceCash.io platformuna davetiniz hala geçerli:%0A${invite.invite_link}`, '_blank');
                          }}
                          className="text-green-600 hover:bg-green-50"
                          title="Email gönder"
                        >
                          <MailIcon className="w-4 h-4" />
                        </Button>
                        {(invite.status === 'pending' || invite.status === 'sent') && (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('ultra_admin_token');
                                const response = await axios.post(
                                  `${BACKEND_URL}/api/ultra-admin/invites/${invite.id}/resend`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                
                                toast.success(`Davet başarıyla yeniden gönderildi: ${invite.email}`);
                                
                                // Refresh invites list
                                fetchInvitedTenants();
                              } catch (error) {
                                console.error('Resend invite error:', error);
                                toast.error(error.response?.data?.detail || 'Davet yeniden gönderilemedi');
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            title="Yeniden Gönder"
                          >
                            <RefreshCwIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {invitedTenants.length === 0 && (
            <div className="text-center py-12">
              <MailIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Davet edilmiş kiracı yok</h3>
              <p className="text-gray-500">Henüz hiç davet gönderilmemiş.</p>
            </div>
          )}
        </Card>
      )}

      {/* Pagination */}
      {activeTab === 'active' && totalPages > 1 && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {indexOfFirstTenant + 1}-{Math.min(indexOfLastTenant, filteredTenants.length)} / {filteredTenants.length} kiracı
            </p>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Önceki
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                    return false;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <Button
                        size="sm"
                        variant={currentPage === page ? 'default' : 'outline'}
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? 'bg-purple-600' : ''}
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {activeTab === 'active' && currentTenants.length === 0 && (
        <Card className="p-12">
          <div className="text-center">
            <BuildingIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Kiracı bulunamadı</h3>
            <p className="text-gray-500">Arama kriterlerinize uygun kiracı bulunmuyor.</p>
          </div>
        </Card>
      )}

      {/* Davet Edilenler Tab */}
      {activeTab === 'invited' && (
        <>
          {loading ? (
            <Card className="p-12">
              <div className="text-center">
                <RefreshCwIcon className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Davetler yükleniyor...</p>
              </div>
            </Card>
          ) : invitedTenants.length === 0 ? (
            <Card className="p-12">
              <div className="text-center">
                <MailIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 mb-2">Davet bulunamadı</h3>
                <p className="text-gray-500 mb-4">Henüz gönderilmiş davet bulunmuyor.</p>
                <Button
                  onClick={() => setInviteModal({ show: true, step: 'form', formData: {}, result: null })}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <SendIcon className="w-4 h-4 mr-2" />
                  İlk Davetinizi Gönderin
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {invitedTenants.map(invite => (
                <Card key={invite.id} className="p-5 hover:shadow-lg transition-shadow">
                  <div className="space-y-4">
                    {/* Invite Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <MailIcon className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{invite.email}</h4>
                          <p className="text-sm text-gray-500">{invite.company_name || 'Şirket adı bekleniyor'}</p>
                        </div>
                      </div>
                      <Badge className={
                        (invite.status === 'pending' || invite.status === 'sent') ? 'bg-yellow-100 text-yellow-800' :
                        invite.status === 'registered' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {(invite.status === 'pending' || invite.status === 'sent') ? '⏳ Bekliyor' :
                         invite.status === 'registered' ? '✅ Kayıt Oldu' : '❌ Süresi Doldu'}
                      </Badge>
                    </div>

                    {/* Invite Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">👥 Max Kullanıcı:</span>
                        <span className="font-medium text-gray-900">{invite.max_users}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">🎁 Deneme Süresi:</span>
                        <span className="font-medium text-gray-900">{invite.trial_days} gün</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">📅 Gönderildi:</span>
                        <span className="font-medium text-gray-900">
                          {new Date(invite.created_at).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">⏰ Son Tarih:</span>
                        <span className="font-medium text-gray-900">
                          {new Date(invite.expires_at).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col space-y-2 pt-2">
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            const inviteLink = `${window.location.origin}/register-company?invite=${invite.token}`;
                            navigator.clipboard.writeText(inviteLink);
                            toast.success('Davet linki kopyalandı!');
                          }}
                          disabled={invite.status !== 'pending' && invite.status !== 'sent'}
                        >
                          <CopyIcon className="w-4 h-4 mr-1" />
                          Linki Kopyala
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            const inviteLink = `${window.location.origin}/register-company?invite=${invite.token}`;
                            const whatsappMessage = `🚀 AdvanceCash.io Özel Davet!\n\nMerhaba! Size ${invite.trial_days} gün ücretsiz deneme içeren özel bir davet gönderiyorum.\n\n${invite.max_users} kullanıcıya kadar erişim!\n\nDavet Linki: ${inviteLink}`;
                            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
                            window.open(whatsappUrl, '_blank');
                          }}
                          disabled={invite.status !== 'pending' && invite.status !== 'sent'}
                        >
                          <MessageCircleIcon className="w-4 h-4 mr-1" />
                          WhatsApp
                        </Button>
                      </div>
                      
                      {/* Resend Invite Button - Only for pending/sent invites */}
                      {(invite.status === 'pending' || invite.status === 'sent') && (
                        <>
                          <Button
                            size="sm"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('ultra_admin_token');
                                const response = await axios.post(
                                  `${BACKEND_URL}/api/ultra-admin/invites/${invite.id}/resend`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                
                                toast.success(`Davet başarıyla yeniden gönderildi: ${invite.email}`);
                                
                                // Refresh invites list
                                fetchInvitedTenants();
                              } catch (error) {
                                console.error('Resend invite error:', error);
                                toast.error(error.response?.data?.detail || 'Davet yeniden gönderilemedi');
                              }
                            }}
                          >
                            <RefreshCwIcon className="w-4 h-4 mr-1" />
                            Yeniden Gönder
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                            onClick={async () => {
                              if (!window.confirm(`"${invite.email}" adresine gönderilen daveti iptal etmek istediğinize emin misiniz?`)) {
                                return;
                              }
                              
                              try {
                                const token = localStorage.getItem('ultra_admin_token');
                                await axios.delete(
                                  `${BACKEND_URL}/api/ultra-admin/invites/${invite.id}`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                
                                toast.success(`Davet başarıyla iptal edildi: ${invite.email}`);
                                
                                // Refresh invites list
                                fetchInvitedTenants();
                              } catch (error) {
                                console.error('Cancel invite error:', error);
                                toast.error(error.response?.data?.detail || 'Davet iptal edilemedi');
                              }
                            }}
                          >
                            <XCircleIcon className="w-4 h-4 mr-1" />
                            İptal Et
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-center">
              {/* Icon based on status */}
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                confirmModal.status === 'success' ? 'bg-green-100' :
                confirmModal.status === 'error' ? 'bg-red-100' :
                confirmModal.action === 'delete' ? 'bg-red-100' : 
                confirmModal.action === 'suspend' ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                {confirmModal.status === 'success' ? (
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : confirmModal.status === 'error' ? (
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : confirmModal.action === 'delete' ? (
                  <TrashIcon className="w-8 h-8 text-red-600" />
                ) : confirmModal.action === 'suspend' ? (
                  <PauseIcon className="w-8 h-8 text-yellow-600" />
                ) : (
                  <PlayIcon className="w-8 h-8 text-green-600" />
                )}
              </div>
              
              {/* Title based on status */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.status === 'success' ? 'İşlem Başarılı!' :
                 confirmModal.status === 'error' ? 'İşlem Başarısız' :
                 confirmModal.action === 'delete' ? 'Kiracıyı Sil' : 
                 confirmModal.action === 'suspend' ? 'Kiracıyı Askıya Al' : 'Kiracıyı Aktifleştir'}
              </h3>
              
              {/* Message based on status */}
              {confirmModal.status === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-700 font-medium">
                    {confirmModal.message}
                  </p>
                </div>
              ) : confirmModal.status === 'error' ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-700 font-medium mb-2">
                    {confirmModal.message}
                  </p>
                  {confirmModal.errorReason && (
                    <p className="text-sm text-red-600 mt-2">
                      <strong>Neden:</strong> {confirmModal.errorReason}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    <strong>{confirmModal.tenantName}</strong> kiracısını {
                      confirmModal.action === 'delete' ? 'silmek' :
                      confirmModal.action === 'suspend' ? 'askıya almak' : 'aktifleştirmek'
                    } istediğinizden emin misiniz?
                  </p>

                  {confirmModal.action === 'delete' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-700">
                        ⚠️ Bu işlem geri alınabilir. Silinen Kiracılar sekmesinden geri yükleyebilirsiniz.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Buttons - only show if not in success/error status */}
              {!confirmModal.status ? (
                <div className="flex space-x-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={closeConfirmModal}
                  >
                    İptal
                  </Button>
                  <Button 
                    className={`flex-1 ${
                      confirmModal.action === 'delete' ? 'bg-red-600 hover:bg-red-700' :
                      confirmModal.action === 'suspend' ? 'bg-yellow-600 hover:bg-yellow-700' :
                      'bg-green-600 hover:bg-green-700'
                    } text-white`}
                    onClick={() => handleTenantAction(confirmModal.tenantId, confirmModal.action)}
                  >
                    {confirmModal.action === 'delete' ? 'Evet, Sil' :
                     confirmModal.action === 'suspend' ? 'Evet, Askıya Al' : 'Evet, Aktifleştir'}
                  </Button>
                </div>
              ) : confirmModal.status === 'error' ? (
                <Button 
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white"
                  onClick={closeConfirmModal}
                >
                  Kapat
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Tenant Detail Modal */}
      {detailModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Kiracı Detayları</h2>
              <button
                onClick={() => setDetailModal({ show: false, tenant: null, loading: false })}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            {detailModal.loading ? (
              <div className="p-12 text-center">
                <RefreshCwIcon className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Bilgiler yükleniyor...</p>
              </div>
            ) : detailModal.tenant ? (
              <div className="p-6 space-y-6">
                {/* Company Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BuildingIcon className="w-5 h-5 mr-2 text-purple-600" />
                    Şirket Bilgileri
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Kiracı No</p>
                      <p className="font-mono font-semibold text-purple-600">#{detailModal.tenant.tenant_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Şirket Adı</p>
                      <p className="font-semibold">{detailModal.tenant.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">İletişim Email</p>
                      <p className="font-medium">{detailModal.tenant.contact_email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">İletişim Sorumlusu</p>
                      <p className="font-medium">{detailModal.tenant.contact_person || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Telefon</p>
                      <p className="font-medium">{detailModal.tenant.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Website</p>
                      <p className="font-medium">{detailModal.tenant.website || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Adres</p>
                      <p className="font-medium">{detailModal.tenant.address || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Şehir</p>
                      <p className="font-medium">{detailModal.tenant.city || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Posta Kodu</p>
                      <p className="font-medium">{detailModal.tenant.postal_code || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vergi Numarası</p>
                      <p className="font-medium">{detailModal.tenant.tax_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vergi Dairesi</p>
                      <p className="font-medium">{detailModal.tenant.tax_office || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Sektör</p>
                      <p className="font-medium">{detailModal.tenant.industry || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Çalışan Sayısı</p>
                      <p className="font-medium">{detailModal.tenant.employee_count || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Subscription Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CreditCardIcon className="w-5 h-5 mr-2 text-purple-600" />
                    Abonelik Bilgileri
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Abonelik Planı</p>
                      <p className="font-semibold">{detailModal.tenant.subscription_plan || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Abonelik Tipi</p>
                      <p className="font-medium">{detailModal.tenant.subscription_type || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Kullanıcı Limiti</p>
                      <p className="font-medium">{detailModal.tenant.user_limit || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Aylık Ücret</p>
                      <p className="font-semibold text-green-600">₺{detailModal.tenant.monthly_fee || '0'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Ödeme Durumu</p>
                      <p className="font-medium">{detailModal.tenant.payment_status || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Abonelik Başlangıç</p>
                      <p className="font-medium">
                        {detailModal.tenant.subscription_start 
                          ? new Date(detailModal.tenant.subscription_start).toLocaleDateString('tr-TR')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Abonelik Bitiş</p>
                      <p className="font-medium">
                        {detailModal.tenant.subscription_end 
                          ? new Date(detailModal.tenant.subscription_end).toLocaleDateString('tr-TR')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Deneme Süresi Bitiş</p>
                      <p className="font-medium">
                        {detailModal.tenant.trial_end 
                          ? new Date(detailModal.tenant.trial_end).toLocaleDateString('tr-TR')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CalendarIcon className="w-5 h-5 mr-2 text-purple-600" />
                    Ek Bilgiler
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Kayıt Tarihi</p>
                      <p className="font-medium">
                        {detailModal.tenant.created_at 
                          ? new Date(detailModal.tenant.created_at).toLocaleDateString('tr-TR')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Durum</p>
                      <p className="font-medium">{detailModal.tenant.is_active ? '✅ Aktif' : '❌ Pasif'}</p>
                    </div>
                    {detailModal.tenant.ultra_admin_invite && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500">Ultra Admin Davet Bilgisi</p>
                        <div className="mt-1 text-sm bg-purple-50 border border-purple-200 rounded p-2">
                          <p>Davet ID: {detailModal.tenant.ultra_admin_invite.invite_id}</p>
                          <p>Davet Eden: {detailModal.tenant.ultra_admin_invite.invited_by}</p>
                          <p>Kabul Tarihi: {new Date(detailModal.tenant.ultra_admin_invite.accepted_at).toLocaleDateString('tr-TR')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Features */}
                {detailModal.tenant.features && detailModal.tenant.features.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CheckIcon className="w-5 h-5 mr-2 text-purple-600" />
                      Özellikler
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {detailModal.tenant.features.map((feature, idx) => (
                        <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                          ✓ {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-12 text-center">
                <p className="text-gray-600">Bilgiler yüklenemedi</p>
              </div>
            )}

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <Button
                onClick={() => setDetailModal({ show: false, tenant: null, loading: false })}
                variant="outline"
              >
                Kapat
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Users Modal */}
      {usersModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Kullanıcılar</h2>
                <p className="text-sm text-gray-500">{usersModal.companyName}</p>
              </div>
              <button
                onClick={() => setUsersModal({ show: false, users: [], companyName: '', loading: false })}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            {usersModal.loading ? (
              <div className="p-12 text-center">
                <RefreshCwIcon className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Kullanıcılar yükleniyor...</p>
              </div>
            ) : usersModal.users.length > 0 ? (
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kullanıcı</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departman</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kayıt Tarihi</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {usersModal.users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`relative w-10 h-10 rounded-full flex items-center justify-center ${
                                user.is_super_admin 
                                  ? 'bg-gradient-to-br from-yellow-400 to-amber-500 ring-2 ring-yellow-300 ring-offset-2' 
                                  : 'bg-gradient-to-br from-purple-500 to-purple-600'
                              }`}>
                                {user.is_super_admin && (
                                  <CrownIcon className="absolute -top-2 -right-1 w-4 h-4 text-yellow-500" fill="currentColor" />
                                )}
                                <span className="text-white font-semibold">
                                  {user.full_name?.charAt(0).toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">{user.full_name || 'N/A'}</p>
                                  {user.is_super_admin && (
                                    <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-xs px-2 py-0.5 flex items-center gap-1">
                                      <CrownIcon className="w-3 h-3" fill="currentColor" />
                                      Süper Admin
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500">{user.employee_id || 'ID yok'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-gray-900">{user.email}</td>
                          <td className="px-4 py-4">
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                              {user.role_name || user.role_id || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-gray-600">{user.department || 'N/A'}</td>
                          <td className="px-4 py-4">
                            {user.is_active ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                ✓ Aktif
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                ✗ Pasif
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-gray-600 text-sm">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : 'N/A'}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                              <button
                                onClick={() => handleLoginAsUser(user)}
                                disabled={!user.is_active}
                                className="px-2.5 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs font-medium rounded hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center gap-1"
                                title="Bu kullanıcı olarak giriş yap"
                                data-testid={`user-login-as-${user.id}`}
                              >
                                🔐 Giriş
                              </button>
                              {user.is_active ? (
                                <button
                                  onClick={() => openUserAction(user, 'deactivate')}
                                  disabled={user.is_super_admin}
                                  className="px-2.5 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 text-xs font-medium rounded disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-1"
                                  title={user.is_super_admin ? 'Süper admin pasifleştirilemez' : 'Kullanıcıyı pasife al'}
                                  data-testid={`user-deactivate-${user.id}`}
                                >
                                  <PowerIcon className="w-3 h-3" /> Pasif Et
                                </button>
                              ) : (
                                <button
                                  onClick={() => openUserAction(user, 'activate')}
                                  className="px-2.5 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 text-xs font-medium rounded transition-all inline-flex items-center gap-1"
                                  title="Kullanıcıyı aktif et"
                                  data-testid={`user-activate-${user.id}`}
                                >
                                  <PlayIcon className="w-3 h-3" /> Aktif Et
                                </button>
                              )}
                              <button
                                onClick={() => openUserAction(user, 'delete')}
                                disabled={user.is_super_admin}
                                className="px-2.5 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 text-xs font-medium rounded disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-1"
                                title={user.is_super_admin ? 'Süper admin silinemez' : 'Kullanıcıyı sil (işlem yoksa)'}
                                data-testid={`user-delete-${user.id}`}
                              >
                                <TrashIcon className="w-3 h-3" /> Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 text-sm text-gray-600 text-center">
                  Toplam {usersModal.users.length} kullanıcı
                </div>
              </div>
            ) : (
              <div className="p-12 text-center">
                <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Kullanıcı bulunamadı</h3>
                <p className="text-gray-500">Bu kiracıya ait kayıtlı kullanıcı bulunmuyor.</p>
              </div>
            )}

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <Button
                onClick={() => setUsersModal({ show: false, users: [], companyName: '', loading: false, tenantId: null })}
                variant="outline"
              >
                Kapat
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* User Action Confirmation Modal (Pasif Et / Aktif Et / Sil) */}
      {userActionModal.show && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                {userActionModal.action === 'delete' && <><TrashIcon className="w-5 h-5 text-red-600" /> Kullanıcıyı Sil</>}
                {userActionModal.action === 'deactivate' && <><PowerIcon className="w-5 h-5 text-amber-600" /> Kullanıcıyı Pasife Al</>}
                {userActionModal.action === 'activate' && <><PlayIcon className="w-5 h-5 text-green-600" /> Kullanıcıyı Aktif Et</>}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                <strong>{userActionModal.user?.full_name || userActionModal.user?.email}</strong>
              </p>
            </div>

            <div className="p-6 space-y-3">
              {userActionModal.loading && !userActionModal.check ? (
                <div className="text-center py-4 text-gray-500 text-sm">İşlem kayıtları kontrol ediliyor…</div>
              ) : (
                <>
                  {userActionModal.action === 'delete' && userActionModal.check && !userActionModal.check.can_delete && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
                      <div className="flex items-start gap-2">
                        <AlertTriangleIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-amber-800">Bu kullanıcı silinemez</p>
                          <p className="text-amber-700 mt-1">{userActionModal.check.reason}</p>
                          {Object.keys(userActionModal.check.breakdown || {}).length > 0 && (
                            <ul className="mt-2 text-xs text-amber-700 list-disc list-inside">
                              {Object.entries(userActionModal.check.breakdown).map(([k, v]) => (
                                <li key={k}>{k}: <strong>{v}</strong> kayıt</li>
                              ))}
                            </ul>
                          )}
                          <p className="mt-2 text-xs text-amber-800">
                            Bunun yerine kullanıcıyı <strong>pasife alabilirsiniz</strong> — geçmiş verileri korunur.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {userActionModal.action === 'delete' && userActionModal.check?.can_delete && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                      <p className="font-semibold">Bu işlem geri alınamaz.</p>
                      <p className="mt-1">Kullanıcının hesabı ve giriş yetkisi kaldırılacak. İşlem geçmişi bulunmadığı için veri kaybı riski yok.</p>
                    </div>
                  )}
                  {userActionModal.action === 'deactivate' && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                      <p>Kullanıcı giriş yapamayacak ancak geçmiş verileri korunacak. İstediğiniz zaman tekrar aktif edebilirsiniz.</p>
                      {userActionModal.check?.transaction_count > 0 && (
                        <p className="mt-2 text-xs">
                          Bu kullanıcının <strong>{userActionModal.check.transaction_count}</strong> işlem kaydı bulunuyor.
                        </p>
                      )}
                    </div>
                  )}
                  {userActionModal.action === 'activate' && (
                    <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                      <p>Kullanıcı tekrar giriş yapabilecek ve mevcut yetkileriyle sisteme erişebilecek.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-t bg-gray-50 px-6 py-3 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setUserActionModal({ show: false, user: null, action: null, check: null, loading: false })}
                data-testid="user-action-cancel"
              >
                İptal
              </Button>
              {/* Delete disabled if user has transactions */}
              {userActionModal.action === 'delete' && !userActionModal.check?.can_delete && userActionModal.check && (
                <Button
                  onClick={() => setUserActionModal((prev) => ({ ...prev, action: 'deactivate' }))}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="user-action-switch-deactivate"
                >
                  <PowerIcon className="w-4 h-4 mr-1" /> Pasife Al
                </Button>
              )}
              {userActionModal.action === 'delete' && userActionModal.check?.can_delete && (
                <Button
                  onClick={executeUserAction}
                  disabled={userActionModal.loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="user-action-confirm"
                >
                  <TrashIcon className="w-4 h-4 mr-1" /> Kalıcı Olarak Sil
                </Button>
              )}
              {userActionModal.action === 'deactivate' && (
                <Button
                  onClick={executeUserAction}
                  disabled={userActionModal.loading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="user-action-confirm"
                >
                  <PowerIcon className="w-4 h-4 mr-1" /> Pasife Al
                </Button>
              )}
              {userActionModal.action === 'activate' && (
                <Button
                  onClick={executeUserAction}
                  disabled={userActionModal.loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="user-action-confirm"
                >
                  <PlayIcon className="w-4 h-4 mr-1" /> Aktif Et
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {inviteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {inviteModal.step === 'form' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Yeni Kiracı Davet Et</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetInviteModal}
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta Adresi *
                    </label>
                    <Input
                      type="email"
                      placeholder="ornek@sirket.com"
                      value={inviteModal.data?.email || ''}
                      onChange={(e) => updateInviteData('email', e.target.value)}
                      onBlur={handleEmailBlur}
                      className={`w-full ${inviteModal.emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {inviteModal.emailError && (
                      <p className="text-red-600 text-sm mt-1">{inviteModal.emailError}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maksimum Kullanıcı Sayısı
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={inviteModal.data.maxUsers}
                        onChange={(e) => updateInviteData('maxUsers', parseInt(e.target.value) || 1)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Deneme Süresi (Gün)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        max="365"
                        value={inviteModal.data.trialDays}
                        onChange={(e) => updateInviteData('trialDays', parseInt(e.target.value) || 0)}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Özel Mesaj (Opsiyonel)
                    </label>
                    <textarea
                      rows={4}
                      placeholder="Davet e-postasına eklenecek özel mesaj..."
                      value={inviteModal.data.message}
                      onChange={(e) => updateInviteData('message', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={resetInviteModal}
                  >
                    İptal
                  </Button>
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleInviteSubmit}
                    disabled={inviteModal.loading || inviteModal.emailError || !inviteModal.data?.email?.trim()}
                  >
                    {inviteModal.loading ? (
                      <>
                        <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <SendIcon className="w-4 h-4 mr-2" />
                        Davet Gönder
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}

            {inviteModal.step === 'success' && inviteModal.result && (
              <>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Davet Başarıyla Gönderildi!</h3>
                  <p className="text-gray-600 mb-6">
                    {inviteModal.data?.email || 'Kullanıcı'} adresine davet e-postası gönderildi.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Davet Detayları:</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Davet ID:</span>
                      <span className="font-mono text-gray-900">{inviteModal.result.invite_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">E-posta:</span>
                      <span className="text-gray-900">{inviteModal.result.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Maksimum Kullanıcı:</span>
                      <span className="text-gray-900">{inviteModal.result.max_users}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deneme Süresi:</span>
                      <span className="text-gray-900">{inviteModal.result.trial_days} gün</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Son Geçerlilik:</span>
                      <span className="text-gray-900">
                        {new Date(inviteModal.result.expires_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-900 mb-2">Davet Linki:</h4>
                  <div className="flex items-center space-x-2">
                    <Input
                      value={inviteModal.result.invite_link}
                      readOnly
                      className="flex-1 text-sm bg-white"
                    />
                    <Button
                      size="sm"
                      onClick={() => copyInviteLink(inviteModal.result.invite_link)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <CopyIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        const whatsappMessage = `🚀 AdvanceCash.io Özel Davet!\n\nMerhaba! Size ${inviteModal.result.trial_days} gün ücretsiz Enterprise deneme süresi içeren özel bir davet gönderiyorum.\n\n${inviteModal.result.max_users} kullanıcıya kadar ücretsiz erişim imkanı!\n\nDavet Linki: ${inviteModal.result.invite_link}\n\nAvans yönetiminde yeni bir deneyim sizi bekliyor! 🎯`;
                        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
                        window.open(whatsappUrl, '_blank');
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <MessageCircleIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    Bu linki manuel olarak paylaşabilir veya e-posta gönderimini bekleyebilirsiniz.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={resetInviteModal}
                  >
                    Kapat
                  </Button>
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => setInviteModal(prev => ({ ...prev, step: 'form', result: null }))}
                  >
                    Yeni Davet Gönder
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Yeni Kiracı Oluştur Modal */}
      {createTenantModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Yeni Kiracı Oluştur</h2>
                  <p className="text-gray-600 mt-1">
                    {createTenantModal.step === 'company' && 'Adım 1/2: Şirket Bilgileri'}
                    {createTenantModal.step === 'admin' && 'Adım 2/2: Admin Kullanıcı'}
                    {createTenantModal.step === 'success' && 'Başarılı!'}
                  </p>
                </div>
                <button onClick={resetCreateTenantModal} className="text-gray-400 hover:text-gray-600">
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Step 1: Şirket Bilgileri */}
              {createTenantModal.step === 'company' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="companyName">Şirket Adı <span className="text-red-500">*</span></Label>
                    <Input
                      id="companyName"
                      value={createTenantModal.data.companyName}
                      onChange={(e) => setCreateTenantModal(prev => ({
                        ...prev,
                        data: { ...prev.data, companyName: e.target.value },
                        errors: { ...prev.errors, companyName: '' }
                      }))}
                      placeholder="Örn: Acme Corp"
                      className={createTenantModal.errors.companyName ? 'border-red-500' : ''}
                    />
                    {createTenantModal.errors.companyName && (
                      <p className="text-red-500 text-sm mt-1">{createTenantModal.errors.companyName}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="taxNumber">Vergi Numarası</Label>
                      <Input
                        id="taxNumber"
                        value={createTenantModal.data.taxNumber}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, taxNumber: e.target.value }
                        }))}
                        placeholder="1234567890"
                      />
                    </div>
                    <div>
                      <Label htmlFor="taxOffice">Vergi Dairesi</Label>
                      <Input
                        id="taxOffice"
                        value={createTenantModal.data.taxOffice}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, taxOffice: e.target.value }
                        }))}
                        placeholder="Kadıköy"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">Adres</Label>
                    <textarea
                      id="address"
                      value={createTenantModal.data.address}
                      onChange={(e) => setCreateTenantModal(prev => ({
                        ...prev,
                        data: { ...prev.data, address: e.target.value }
                      }))}
                      placeholder="Şirket adresi"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Telefon <span className="text-red-500">*</span></Label>
                      <Input
                        id="phone"
                        value={createTenantModal.data.phone}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, phone: e.target.value },
                          errors: { ...prev.errors, phone: '' }
                        }))}
                        placeholder="+90 212 555 0000"
                        className={createTenantModal.errors.phone ? 'border-red-500' : ''}
                      />
                      {createTenantModal.errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{createTenantModal.errors.phone}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="companyEmail">Şirket Email <span className="text-red-500">*</span></Label>
                      <Input
                        id="companyEmail"
                        type="email"
                        value={createTenantModal.data.email}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, email: e.target.value },
                          errors: { ...prev.errors, email: '' }
                        }))}
                        placeholder="info@company.com"
                        className={createTenantModal.errors.email ? 'border-red-500' : ''}
                      />
                      {createTenantModal.errors.email && (
                        <p className="text-red-500 text-sm mt-1">{createTenantModal.errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="maxUsers">Maksimum Kullanıcı</Label>
                      <Input
                        id="maxUsers"
                        type="number"
                        min="1"
                        value={createTenantModal.data.maxUsers}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, maxUsers: parseInt(e.target.value) || 10 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="trialDays">Deneme Süresi (Gün)</Label>
                      <Input
                        id="trialDays"
                        type="number"
                        min="0"
                        value={createTenantModal.data.trialDays}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, trialDays: parseInt(e.target.value) || 14 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="plan">Plan</Label>
                      <select
                        id="plan"
                        value={createTenantModal.data.plan}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, plan: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="trial">Deneme</option>
                        <option value="basic">Temel</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Kurumsal</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Admin Kullanıcı */}
              {createTenantModal.step === 'admin' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>{createTenantModal.data.companyName}</strong> şirketi için admin kullanıcı oluşturuluyor
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="adminFullName">Ad Soyad <span className="text-red-500">*</span></Label>
                    <Input
                      id="adminFullName"
                      value={createTenantModal.data.adminFullName}
                      onChange={(e) => setCreateTenantModal(prev => ({
                        ...prev,
                        data: { ...prev.data, adminFullName: e.target.value },
                        errors: { ...prev.errors, adminFullName: '' }
                      }))}
                      placeholder="Ahmet Yılmaz"
                      className={createTenantModal.errors.adminFullName ? 'border-red-500' : ''}
                    />
                    {createTenantModal.errors.adminFullName && (
                      <p className="text-red-500 text-sm mt-1">{createTenantModal.errors.adminFullName}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="adminEmail">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="adminEmail"
                      type="email"
                      value={createTenantModal.data.adminEmail}
                      onChange={(e) => setCreateTenantModal(prev => ({
                        ...prev,
                        data: { ...prev.data, adminEmail: e.target.value },
                        errors: { ...prev.errors, adminEmail: '' }
                      }))}
                      placeholder="admin@company.com"
                      className={createTenantModal.errors.adminEmail ? 'border-red-500' : ''}
                    />
                    {createTenantModal.errors.adminEmail && (
                      <p className="text-red-500 text-sm mt-1">{createTenantModal.errors.adminEmail}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="adminPassword">Şifre <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Input
                        id="adminPassword"
                        type={createTenantModal.showPassword ? "text" : "password"}
                        value={createTenantModal.data.adminPassword}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, adminPassword: e.target.value },
                          errors: { ...prev.errors, adminPassword: '' }
                        }))}
                        placeholder="En az 6 karakter"
                        className={createTenantModal.errors.adminPassword ? 'border-red-500 pr-10' : 'pr-10'}
                      />
                      <button
                        type="button"
                        onClick={() => setCreateTenantModal(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {createTenantModal.showPassword ? (
                          <EyeOffIcon className="w-5 h-5" />
                        ) : (
                          <EyeIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {createTenantModal.errors.adminPassword && (
                      <p className="text-red-500 text-sm mt-1">{createTenantModal.errors.adminPassword}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="adminPasswordConfirm">Şifre Tekrar <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Input
                        id="adminPasswordConfirm"
                        type={createTenantModal.showPasswordConfirm ? "text" : "password"}
                        value={createTenantModal.data.adminPasswordConfirm}
                        onChange={(e) => setCreateTenantModal(prev => ({
                          ...prev,
                          data: { ...prev.data, adminPasswordConfirm: e.target.value },
                          errors: { ...prev.errors, adminPasswordConfirm: '' }
                        }))}
                        placeholder="Şifreyi tekrar girin"
                        className={createTenantModal.errors.adminPasswordConfirm ? 'border-red-500 pr-10' : 'pr-10'}
                      />
                      <button
                        type="button"
                        onClick={() => setCreateTenantModal(prev => ({ ...prev, showPasswordConfirm: !prev.showPasswordConfirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {createTenantModal.showPasswordConfirm ? (
                          <EyeOffIcon className="w-5 h-5" />
                        ) : (
                          <EyeIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {createTenantModal.errors.adminPasswordConfirm && (
                      <p className="text-red-500 text-sm mt-1">{createTenantModal.errors.adminPasswordConfirm}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {createTenantModal.step === 'success' && (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Kiracı Başarıyla Oluşturuldu!</h3>
                  <p className="text-gray-600 mb-6">
                    <strong>{createTenantModal.result?.company_name}</strong> şirketi ve admin kullanıcısı oluşturuldu.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                    <p className="text-sm text-gray-700 mb-2"><strong>Şirket ID:</strong> {createTenantModal.result?.company_id}</p>
                    <p className="text-sm text-gray-700 mb-2"><strong>Admin Email:</strong> {createTenantModal.result?.admin_email}</p>
                    <p className="text-sm text-gray-700"><strong>Kullanıcı Sayısı:</strong> {createTenantModal.result?.user_count || 1}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  if (createTenantModal.step === 'admin') {
                    setCreateTenantModal(prev => ({ ...prev, step: 'company', errors: {} }));
                  } else if (createTenantModal.step === 'success') {
                    resetCreateTenantModal();
                  } else {
                    resetCreateTenantModal();
                  }
                }}
                disabled={createTenantModal.loading}
              >
                {createTenantModal.step === 'success' ? 'Kapat' : createTenantModal.step === 'admin' ? 'Geri' : 'İptal'}
              </Button>
              {createTenantModal.step !== 'success' && (
                <Button
                  onClick={handleCreateTenant}
                  disabled={createTenantModal.loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {createTenantModal.loading ? 'Oluşturuluyor...' : createTenantModal.step === 'company' ? 'İleri' : 'Oluştur'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraTenantManagementNew;
